import { DashboardRecord } from "@/shared/domain/dashboards";

/**
 * Landing-time dashboard selection for the metrics pages.
 *
 * Two cooperating pieces live here:
 *
 * 1. The REMEMBERED-CHOICE STORE — the user's explicitly chosen dashboard id,
 *    one value per org in localStorage. It is written ONLY when the user saves
 *    a dashboard (including Save-As) or loads one from the dashboard menu;
 *    deep links, the "New … dashboard" actions, and automatic default
 *    landings never write it. The key carries no device segment (unlike `draftKeyFor`): the
 *    remembered selection is shared by the org-level page and every device
 *    Metrics tab.
 *
 * 2. The RESOLVER (`resolveInitialSelection`) — a pure decision function that
 *    picks what a bare metrics route (no `:dashboardId` path segment) should
 *    show, in priority order:
 *
 *    - priority 1: the remembered selection. A context DRAFT counts as the
 *      remembered selection by mere presence (rows D1/D2): a draft on saved
 *      dashboard X lands on X (beating a differing stored id, with the
 *      draft seeding the canvas directly), and an unsaved
 *      new-dashboard draft lands back on the unsaved view.
 *    - priority 2: the default pick ({@link defaultDashboardPick}) when
 *      dashboards exist but nothing is remembered.
 *    - priority 3: the sample-dashboard seed layout, ONLY when no
 *      user-visible dashboard exists (or the list itself failed to load —
 *      the one documented exception, since the precondition can't be
 *      evaluated).
 *
 *    Priority 4 (metrics onboarding when the org has no metric definitions)
 *    is owned by `MetricsDashboard`'s existing render gate, not this module.
 */

const SELECTION_KEY_PREFIX = "roboto.metricsDashboard.lastSelection";

function selectionKeyFor(orgId: string): string {
  return `${SELECTION_KEY_PREFIX}:${orgId}`;
}

/**
 * The remembered dashboard id for `orgId`, or `null` when none is stored. A
 * blank/whitespace-only value is treated as corrupt and removed — the stored
 * state is a plain dashboard id or absent, nothing else.
 */
export function loadRememberedDashboardId(orgId: string): string | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(selectionKeyFor(orgId));
  } catch {
    return null; // Storage unavailable (privacy mode); behave as unset.
  }
  if (raw === null) {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    clearRememberedDashboardId(orgId);
    return null;
  }
  return trimmed;
}

/** Record `dashboardId` as the org's remembered selection (best-effort). */
export function rememberDashboardId(orgId: string, dashboardId: string): void {
  try {
    localStorage.setItem(selectionKeyFor(orgId), dashboardId);
  } catch {
    // Best-effort only.
  }
}

/** Remove the org's remembered selection, if any (best-effort). */
export function clearRememberedDashboardId(orgId: string): void {
  try {
    localStorage.removeItem(selectionKeyFor(orgId));
  } catch {
    // Best-effort only.
  }
}

/** Compare ISO-8601 timestamps as epoch ms (robust to representation drift —
 *  e.g. `+00:00` vs `Z` — which string comparison is not). Unparseable values
 *  sort as 0 so a corrupt record never wins a recency contest. */
function epochMs(isoTimestamp: string): number {
  const ms = Date.parse(isoTimestamp);
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * The dashboard a bare landing defaults to when nothing is remembered
 * (priority 2): the user's most-recently-MODIFIED personal dashboard, or —
 * only when the user has no personal dashboards — the OLDEST (earliest
 * `created`) org-wide dashboard.
 *
 * "Personal" is any dashboard NOT owned by the org principal
 * (`owner_principal_id !== \`org:\${org_id}\``) — ownership is the sole
 * org-wide/personal signal, and in the default (non-`include_all`) listing
 * every non-org-owned dashboard is the caller's own, so no caller comparison
 * is needed. Ties on the relevant timestamp break by `dashboard_id`
 * ascending, so the pick is deterministic across renders and clients.
 *
 * `dashboards` must be non-empty.
 */
export function defaultDashboardPick(
  dashboards: readonly DashboardRecord[],
): DashboardRecord {
  const personal = dashboards.filter(
    (d) => d.owner_principal_id !== `org:${d.org_id}`,
  );
  if (personal.length > 0) {
    // Most recently modified; tie → dashboard_id ascending.
    return personal.reduce((best, next) => {
      const bestMs = epochMs(best.modified);
      const nextMs = epochMs(next.modified);
      if (nextMs > bestMs) {
        return next;
      }
      if (nextMs === bestMs && next.dashboard_id < best.dashboard_id) {
        return next;
      }
      return best;
    });
  }
  // Oldest org-wide; tie → dashboard_id ascending.
  return dashboards.reduce((best, next) => {
    const bestMs = epochMs(best.created);
    const nextMs = epochMs(next.created);
    if (nextMs < bestMs) {
      return next;
    }
    if (nextMs === bestMs && next.dashboard_id < best.dashboard_id) {
      return next;
    }
    return best;
  });
}

/** The drafts present in the CURRENT scope (org page, or one device tab),
 *  discovered by `findScopeDrafts` in `dashboardDraft.ts`. */
export interface ScopeDrafts {
  /** An unsaved-view (new-dashboard) draft exists in this scope. */
  unsaved: boolean;
  /** Saved-dashboard ids with a stored draft in this scope. */
  savedDashboardIds: string[];
}

export type InitialSelection =
  /** Inputs not ready (list still loading) — keep the spinner up. */
  | { kind: "pending" }
  /** Priority 3: seed the unsaved view with the sample dashboard layout. */
  | { kind: "sample"; clearRemembered: boolean }
  /** Priority 1 by draft presence: stay on the unsaved view; the existing
   *  in-progress new-dashboard edits seed the canvas on arrival. */
  | { kind: "unsaved-draft" }
  /** Priorities 1–2: navigate to this dashboard. */
  | { kind: "dashboard"; dashboardId: string; clearRemembered: boolean };

export interface ResolveInitialSelectionArgs {
  /** The FULLY drained visible-dashboard list, or `undefined` while it is
   *  still loading/draining. */
  dashboards: readonly DashboardRecord[] | undefined;
  /** True when the list query failed — the sample-seed fail-open exception. */
  dashboardsErrored: boolean;
  /** {@link loadRememberedDashboardId} for the current org. */
  rememberedDashboardId: string | null;
  /** The current scope's drafts ({@link ScopeDrafts}). */
  scopeDrafts: ScopeDrafts;
}

/**
 * Decide what a bare metrics landing shows. Pure and synchronous — evaluated
 * top-down, first match wins:
 *
 * | row | precondition                              | result |
 * |-----|-------------------------------------------|--------|
 * | D1  | unsaved-view draft exists                 | `unsaved-draft` |
 * | E1  | list query errored                        | `sample` (fail-open exception) |
 * | P   | list still loading                        | `pending` |
 * | D2  | a drafted saved dashboard is visible      | that dashboard (draft on the stored id first, else {@link defaultDashboardPick} over the drafted ones) |
 * | R1  | stored id visible                         | that dashboard |
 * | R2  | stored id stale, list non-empty           | {@link defaultDashboardPick}, clear the stored id |
 * | R3  | stored id stale, list empty               | `sample`, clear the stored id |
 * | R4  | nothing stored, list non-empty            | {@link defaultDashboardPick} |
 * | R5  | nothing stored, list empty                | `sample` |
 *
 * Invariant: with a known (non-errored) list, `sample` is returned only
 * when the list is empty. Orphaned drafts (their dashboard is not visible)
 * are skipped, not cleared — visibility can be transient.
 */
export function resolveInitialSelection(
  args: ResolveInitialSelectionArgs,
): InitialSelection {
  const { dashboards, dashboardsErrored, rememberedDashboardId, scopeDrafts } =
    args;

  // D1 — an in-progress new-dashboard draft IS the remembered selection,
  // by presence alone. Needs neither the list nor the stored id.
  if (scopeDrafts.unsaved) {
    return { kind: "unsaved-draft" };
  }

  // E1 — the fail-open exception: without a list the sample precondition
  // can't be evaluated, and the sample seed is the only self-contained
  // default.
  if (dashboardsErrored) {
    return { kind: "sample", clearRemembered: false };
  }

  // P — everything below needs the drained list.
  if (dashboards === undefined) {
    return { kind: "pending" };
  }

  // D2 — a draft on a saved dashboard makes that dashboard the remembered
  // selection, beating a differing stored id. Orphaned drafts are skipped.
  const visibleIds = new Set(dashboards.map((d) => d.dashboard_id));
  const draftedVisible = scopeDrafts.savedDashboardIds.filter((id) =>
    visibleIds.has(id),
  );
  if (draftedVisible.length > 0) {
    const dashboardId =
      rememberedDashboardId !== null &&
      draftedVisible.includes(rememberedDashboardId)
        ? rememberedDashboardId
        : defaultDashboardPick(
            dashboards.filter((d) => draftedVisible.includes(d.dashboard_id)),
          ).dashboard_id;
    return { kind: "dashboard", dashboardId, clearRemembered: false };
  }

  // R1/R2/R3 — the stored remembered id.
  if (rememberedDashboardId !== null) {
    if (visibleIds.has(rememberedDashboardId)) {
      return {
        kind: "dashboard",
        dashboardId: rememberedDashboardId,
        clearRemembered: false,
      };
    }
    // Stale (deleted or no longer visible): fall to priority 2 when possible,
    // and clear the stored id — visibility is org-wide, so it is stale for
    // every scope.
    if (dashboards.length > 0) {
      return {
        kind: "dashboard",
        dashboardId: defaultDashboardPick(dashboards).dashboard_id,
        clearRemembered: true,
      };
    }
    return { kind: "sample", clearRemembered: true };
  }

  // R4/R5 — nothing remembered.
  if (dashboards.length > 0) {
    return {
      kind: "dashboard",
      dashboardId: defaultDashboardPick(dashboards).dashboard_id,
      clearRemembered: false,
    };
  }
  return { kind: "sample", clearRemembered: false };
}
